/**
 * OpenCC 簡繁轉換器的 Lazy Loader。
 *
 * opencc-js 的字典檔約 150-200KB，透過動態 import() 實現按需載入，
 * 只有使用格式調整器的頁面才會下載此 chunk。
 *
 * === 使用方式 ===
 *
 * 1. Controller 的 connect() 呼叫 preloadOpenCC() 啟動背景載入
 * 2. Rule 的 apply() 透過 getConverter() 取得 converter（未載入時回傳 null）
 * 3. preloadOpenCC() 完成後觸發 re-format，確保轉換生效
 */

let converter = null
let loadingPromise = null

/**
 * 取得已載入的簡繁轉換器。
 *
 * @returns {Function|null} 轉換函式（接收字串回傳轉換後字串），未載入時回傳 null
 */
export function getConverter() {
  return converter
}

/**
 * 預載入 opencc-js 並建立 s2tw 轉換器。
 *
 * 使用 Webpack 動態 import() 將 opencc-js 拆為獨立 chunk。
 * 多次呼叫回傳同一個 Promise（冪等）。
 *
 * @returns {Promise<Function>} 轉換函式
 */
export function preloadOpenCC() {
  if (loadingPromise) return loadingPromise

  loadingPromise = import(/* webpackChunkName: "opencc" */ 'opencc-js')
    .then((OpenCC) => {
      // s2tw 模式：簡體→台灣繁體（使用「裡」而非「裏」等台灣慣用字）
      // 但 s2tw 會把「台」轉成「臺」（正式寫法），台灣日常及金融文本普遍用「台」，
      // 因此包裝一層 post-fix 將「臺」轉回「台」
      const rawConverter = OpenCC.Converter({ from: 'cn', to: 'tw' })
      converter = (text) => rawConverter(text).replace(/臺/g, '台')
      return converter
    })
    .catch((error) => {
      // 載入失敗時重設，允許下次重試
      loadingPromise = null
      console.error('Failed to load opencc-js:', error)
      throw error
    })

  return loadingPromise
}

/**
 * 重設內部狀態（僅供測試使用）。
 */
export function _resetForTest() {
  converter = null
  loadingPromise = null
}
